import { Check, ChevronDown } from 'lucide-react'
import type React from 'react'
import { cn } from '../../lib/utils'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './select'

interface MaterialSelectProps {
    value: string
    onChange: (value: string) => void
    options: { value: string; label: string }[]
    className?: string
    disabled?: boolean
    variant?: 'filled' | 'text' | 'custom'
    align?: 'start' | 'center' | 'end' | 'left' | 'right'
    menuClassName?: string
    placeholder?: string
    icon?: React.ReactNode
}

export const MaterialSelect: React.FC<MaterialSelectProps> = ({
    value,
    onChange,
    options,
    className = '',
    disabled = false,
    variant = 'filled',
    align = 'start', // Shadcn uses start/center/end
    menuClassName = '',
    placeholder = 'Select...',
    icon,
}) => {
    // Map legacy 'left'/'right' to Shadcn 'start'/'end' if needed, but strict types prefer start/end
    const alignProp = align === 'left' ? 'start' : align === 'right' ? 'end' : align

    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger
                className={cn(
                    variant !== 'custom' && 'h-auto min-h-[32px] gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all outline-none border-none focus:ring-0',
                    variant === 'filled'
                        ? 'bg-white/40 dark:bg-slate-800/40 hover:bg-white/60 dark:hover:bg-slate-800/60 backdrop-blur-md border border-white/40 dark:border-slate-700/40 shadow-sm text-slate-700 dark:text-slate-200 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-500/50 ring-1 ring-white/40 dark:ring-white/10'
                        : variant === 'text'
                        ? 'bg-transparent hover:bg-slate-100/50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 p-0'
                        : '',
                    disabled && 'opacity-50 cursor-not-allowed',
                    className,
                )}
                showIcon={false} // We custom render icon if needed or let Shadcn do it? Shadcn Trigger has icon by default.
            >
                <div className="flex items-center justify-between w-full gap-2 outline-none">
                    <div className="flex items-center gap-1.5 flex-1">
                        {icon && <span className="shrink-0">{icon}</span>}
                        <SelectValue placeholder={placeholder} />
                    </div>
                    <ChevronDown size={14} className="opacity-50 shrink-0" />
                </div>
            </SelectTrigger>
            <SelectContent
                align={align as any}
                className={cn(
                    'bg-white/90 dark:bg-slate-800/95 backdrop-blur-2xl rounded-[20px] shadow-2xl shadow-indigo-500/10 border border-white/50 dark:border-slate-700/50 ring-1 ring-black/5 dark:ring-white/5 overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-1.5',
                    menuClassName,
                )}
            >
                <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-0.5">
                    {options.map((option) => (
                        <SelectItem
                            key={option.value}
                            value={option.value}
                            className={cn(
                                'text-[11px] md:text-xs font-black rounded-[14px] cursor-pointer transition-all duration-300 py-2',
                                option.value === value
                                    ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/40 ring-1 ring-white/20 focus:bg-indigo-600 focus:text-white dark:focus:bg-indigo-600 dark:focus:text-white'
                                    : 'text-slate-600 dark:text-slate-300 focus:bg-slate-100 dark:focus:bg-slate-700/50 focus:text-indigo-600 dark:focus:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-700/50',
                            )}
                        >
                            {option.label}
                        </SelectItem>
                    ))}
                </div>
            </SelectContent>
        </Select>
    )
}
